import {
  forwardRef,
  useState,
  useCallback,
  useEffect,
  useId,
  type InputHTMLAttributes,
} from 'react'
import { clsx } from 'clsx'

export interface CurrencyInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string
  error?: string
  hint?: string
  currency?: 'COP'
}

const formatCurrency = (value: string): string => {
  // Remove all non-numeric characters
  const numericValue = value.replace(/\D/g, '')

  if (!numericValue) return ''

  // Format with period as thousand separator
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(parseInt(numericValue, 10))
}

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  ({ className, label, error, hint, id, onChange, value, ...props }, ref) => {
    const generatedId = useId()
    const inputId = id || generatedId
    const [displayValue, setDisplayValue] = useState<string>(
      value ? formatCurrency(String(value)) : ''
    )

    // Sync external value changes (controlled mode)
    useEffect(() => {
      if (value !== undefined) {
        setDisplayValue(formatCurrency(String(value)))
      }
    }, [value])

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value
        const numericOnly = input.replace(/\D/g, '')

        // Update display value with formatting
        const formatted = numericOnly ? formatCurrency(numericOnly) : ''
        setDisplayValue(formatted)

        // Call original onChange with numeric value
        if (onChange) {
          const changeEvent = {
            ...e,
            target: {
              ...e.target,
              value: numericOnly,
            },
          } as React.ChangeEvent<HTMLInputElement>
          onChange(changeEvent)
        }
      },
      [onChange]
    )

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-neutral-700 mb-1.5">
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          type="text"
          inputMode="numeric"
          className={clsx(
            'w-full h-10 px-3 rounded-lg border bg-white text-neutral-900 placeholder:text-neutral-400',
            'transition-colors duration-150',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            error
              ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500/20'
              : 'border-neutral-300 focus:border-primary-500 focus:ring-primary-500/20',
            'disabled:bg-neutral-100 disabled:cursor-not-allowed',
            'font-mono',
            className
          )}
          value={displayValue}
          onChange={handleChange}
          placeholder="$ 0"
          {...props}
        />
        {error && <p className="mt-1.5 text-sm text-danger-600">{error}</p>}
        {hint && !error && <p className="mt-1.5 text-sm text-neutral-500">{hint}</p>}
      </div>
    )
  }
)

CurrencyInput.displayName = 'CurrencyInput'
